import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useBusiness } from '@/context/BusinessContext';
import { apiClient } from '@/api/axios';

export type OnboardingStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED';

export interface OnboardingState {
  status: OnboardingStatus;
  completedSections: {
    basicInformation: boolean;
    businessAddress: boolean;
    primaryContact: boolean;
  };
  lastUpdated?: string;
}

interface OnboardingContextType {
  onboardingState: OnboardingState;
  updateOnboardingSection: (section: keyof OnboardingState['completedSections'], isComplete: boolean) => void;
  isOnboardingComplete: boolean;
  isOnboardingLoaded: boolean;
  getOnboardingProgress: () => { completed: number; total: number; percentage: number };
  forceCompleteOnboarding: () => void;
  resetOnboarding: () => void;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

const DEFAULT_STATE: OnboardingState = {
  status: 'NOT_STARTED',
  completedSections: {
    basicInformation: false,
    businessAddress: false,
    primaryContact: false,
  },
};

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const { business, businessId, isGroupMode } = useBusiness();
  const [onboardingState, setOnboardingState] = useState<OnboardingState>(DEFAULT_STATE);
  const [initialized, setInitialized] = useState(false);

  // In group mode there is no single business to onboard — treat as complete
  useEffect(() => {
    if (isGroupMode) {
      setOnboardingState({
        status: 'COMPLETED',
        completedSections: { basicInformation: true, businessAddress: true, primaryContact: true },
      });
      setInitialized(true);
      return;
    }
  }, [isGroupMode]);

  // Load onboarding state from the business object (which comes from the API)
  useEffect(() => {
    if (isGroupMode) return;
    if (!business) return;
    const saved = business.onboardingState as OnboardingState | undefined;
    if (saved?.completedSections) {
      setOnboardingState({
        status: saved.status as OnboardingStatus ?? 'NOT_STARTED',
        completedSections: {
          basicInformation: saved.completedSections.basicInformation ?? false,
          businessAddress: saved.completedSections.businessAddress ?? false,
          primaryContact: saved.completedSections.primaryContact ?? false,
        },
        lastUpdated: saved.lastUpdated,
      });
    } else {
      setOnboardingState(DEFAULT_STATE);
    }
    setInitialized(true);
  }, [business]);

  // Persist onboarding state to API whenever it changes (after initial load)
  const persistToApi = useCallback(
    async (state: OnboardingState) => {
      if (!businessId || isGroupMode) return;
      try {
        await apiClient.patch(`/pro/businesses/${businessId}`, {
          onboardingState: state,
        });
      } catch (err) {
        console.error('Failed to persist onboarding state:', err);
      }
    },
    [businessId, isGroupMode],
  );

  // Calculate if onboarding is complete
  const isOnboardingComplete =
    onboardingState.completedSections.basicInformation &&
    onboardingState.completedSections.businessAddress &&
    onboardingState.completedSections.primaryContact;

  // Auto-update status based on completed sections
  useEffect(() => {
    if (!initialized) return;

    if (isOnboardingComplete && onboardingState.status !== 'COMPLETED') {
      const updated = {
        ...onboardingState,
        status: 'COMPLETED' as OnboardingStatus,
        lastUpdated: new Date().toISOString(),
      };
      setOnboardingState(updated);
      persistToApi(updated);
    } else if (!isOnboardingComplete && onboardingState.status === 'COMPLETED') {
      const updated = {
        ...onboardingState,
        status: 'IN_PROGRESS' as OnboardingStatus,
        lastUpdated: new Date().toISOString(),
      };
      setOnboardingState(updated);
      persistToApi(updated);
    }
  }, [isOnboardingComplete, onboardingState.status, initialized]);

  const updateOnboardingSection = useCallback(
    (section: keyof OnboardingState['completedSections'], isComplete: boolean) => {
      setOnboardingState((prev) => {
        const hasAnySectionComplete = Object.values({
          ...prev.completedSections,
          [section]: isComplete,
        }).some((v) => v);

        const updated: OnboardingState = {
          ...prev,
          completedSections: {
            ...prev.completedSections,
            [section]: isComplete,
          },
          status:
            hasAnySectionComplete && prev.status === 'NOT_STARTED'
              ? 'IN_PROGRESS'
              : prev.status,
          lastUpdated: new Date().toISOString(),
        };

        persistToApi(updated);
        return updated;
      });
    },
    [persistToApi],
  );

  const getOnboardingProgress = useCallback(() => {
    const sections = Object.values(onboardingState.completedSections);
    const completed = sections.filter(Boolean).length;
    const total = sections.length;
    const percentage = Math.round((completed / total) * 100);
    return { completed, total, percentage };
  }, [onboardingState.completedSections]);

  const forceCompleteOnboarding = useCallback(() => {
    const updated: OnboardingState = {
      status: 'COMPLETED',
      completedSections: {
        basicInformation: true,
        businessAddress: true,
        primaryContact: true,
      },
      lastUpdated: new Date().toISOString(),
    };
    setOnboardingState(updated);
    persistToApi(updated);
  }, [persistToApi]);

  const resetOnboarding = useCallback(() => {
    const updated: OnboardingState = {
      status: 'NOT_STARTED',
      completedSections: {
        basicInformation: false,
        businessAddress: false,
        primaryContact: false,
      },
      lastUpdated: new Date().toISOString(),
    };
    setOnboardingState(updated);
    persistToApi(updated);
  }, [persistToApi]);

  return (
    <OnboardingContext.Provider
      value={{
        onboardingState,
        updateOnboardingSection,
        isOnboardingComplete,
        isOnboardingLoaded: initialized,
        getOnboardingProgress,
        forceCompleteOnboarding,
        resetOnboarding,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return context;
}
